import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trophy, Calendar, BarChart3, ArrowLeft, Bookmark } from 'lucide-react';
import { useChampionship } from '@/contexts/ChampionshipContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import volleyballApi from '@/services/api';

export const ChampionshipDetailPage = () => {
  const navigate = useNavigate();
  const { championshipId } = useParams();
  const { selectChampionship, selectedChampionship, toggleFavorite, isFavorite } = useChampionship();
  const [championship, setChampionship] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChampionshipData();
  }, [championshipId]);

  const loadChampionshipData = async () => {
    try {
      setLoading(true);
      
      // Load championship info
      const champData = await volleyballApi.getChampionship(championshipId);
      setChampionship(champData);
      selectChampionship(champData);

      // Load stats
      const [matches, standings] = await Promise.all([
        volleyballApi.getChampionshipMatches(championshipId),
        volleyballApi.getChampionshipStandings(championshipId),
      ]);

      // Calculate stats
      const playedMatches = matches.filter(m => m.played).length;
      const totalMatches = matches.length;
      const teams = standings.length;
      const leader = standings.length > 0 ? standings[0] : null;

      // Calculate max rounds
      const maxRound = matches.reduce((max, m) => Math.max(max, m.round), 0);

      setStats({
        teams,
        playedMatches,
        totalMatches,
        maxRound,
        leader,
      });
    } catch (error) {
      console.error('Error loading championship data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteClick = () => {
    toggleFavorite(championshipId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!championship) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">Campionato non trovato</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Torna alla selezione
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <ArrowLeft className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="flex items-center space-x-3 flex-1 justify-center">
              <Trophy className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div className="text-center">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  C.R. PUGLIA
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">{championship.season}</p>
              </div>
            </div>
            <button
              onClick={handleFavoriteClick}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <Bookmark
                className={`h-6 w-6 transition-colors ${
                  isFavorite(championshipId)
                    ? 'fill-pink-500 text-pink-500'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              />
            </button>
            <ThemeToggle />
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-700 dark:to-blue-900 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm mb-4">
            <span className="text-sm font-medium">{championship.level}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-2">
            {championship.series} {championship.gender}
          </h2>
          <p className="text-xl text-blue-100">Girone {championship.group}</p>
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="container mx-auto px-4 -mt-8 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                {stats.teams}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Squadre</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                {stats.playedMatches}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Partite Giocate</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                {stats.maxRound}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Giornate Totali</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center">
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-1 truncate">
                {stats.leader ? stats.leader.team : '-'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Leader ({stats.leader ? stats.leader.points : 0} pt)
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="space-y-4">
          <button
            onClick={() => navigate(`/championship/${championshipId}/home`)}
            className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 p-6 flex items-center justify-between group border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400"
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                <Trophy className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Home</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Panoramica e ultimi risultati
                </p>
              </div>
            </div>
            <svg
              className="h-6 w-6 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          <button
            onClick={() => navigate(`/championship/${championshipId}/classifica`)}
            className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 p-6 flex items-center justify-between group border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400"
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
                <BarChart3 className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Classifica</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Visualizza la classifica completa
                </p>
              </div>
            </div>
            <svg
              className="h-6 w-6 text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          <button
            onClick={() => navigate(`/championship/${championshipId}/calendario`)}
            className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 p-6 flex items-center justify-between group border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400"
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors">
                <Calendar className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Calendario</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Tutte le partite per giornata
                </p>
              </div>
            </div>
            <svg
              className="h-6 w-6 text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
